import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { compact } from "lodash";
import { lastValueFrom } from "rxjs";
import { AppointmentBackend } from "src/app/backend/api/appointment.backend";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { MessageBackend } from "src/app/backend/api/message.backend";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { ReminderBackend } from "src/app/backend/api/reminder.backend";
import { Priority } from "src/app/backend/models/message/message.create";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { reminderSendWhatsAppFromCalendar } from "src/app/modules/bootstrap/pms-ui/functions/reminder-send-whatsapp.fucntion";
import { CreateReminderFunctionService } from "src/app/modules/bootstrap/pms-ui/functions/reminder/create-reminder/create-reminder-function.service";
import { DocumentService } from "src/app/services/document.service";
import { RegionService } from "src/app/services/feature-access-control/region.service";
import { ToastService } from "src/app/services/toast.service";
import { getCustomerNominative } from "src/app/support/customer";
import { dateUtil } from "src/app/support/date-util";
import { sntTranslate } from "src/app/support/translate";

type Input = {
  appointmentId: string;
};

@Injectable()
export class NotifyAppointmentFunctionService {
  private readonly appointmentBackend = inject(AppointmentBackend);
  private readonly bootstrapService = inject(BootstrapService);
  private readonly regionService = inject(RegionService);
  private readonly customerBackend = inject(CustomerBackend);
  private readonly reminderBackend = inject(ReminderBackend);
  private readonly messageBackend = inject(MessageBackend);
  private readonly patientBackend = inject(PetBackend);
  private readonly toastService = inject(ToastService);
  private readonly documentService = inject(DocumentService);

  private readonly createReminderFunctionService = inject(
    CreateReminderFunctionService
  );

  async execute(input: Input): Promise<void> {
    const appointment = await lastValueFrom(
      this.appointmentBackend.load({ id: input.appointmentId })
    );

    const createReminderResult =
      await this.createReminderFunctionService.execute({
        appointment,
        expireDate: appointment.startAt,
        resources: [appointment.id],
        summary: undefined,
        tenant: this.bootstrapService.getCurrentTenant()!
      });

    if (!createReminderResult) {
      return;
    }

    switch (createReminderResult.transport) {
      case "email": {
        await lastValueFrom(this.appointmentBackend.send(appointment.id));
        break;
      }
      case "unknown": {
        let target = appointment.customer;

        let lastName =
          getCustomerNominative(target) === ""
            ? "cliente"
            : getCustomerNominative(target);

        const templateMessage = sntTranslate(
          marker(
            "<p>Buongiorno customerNominative \nQuesto è un promemoria del suo prossimo appuntamento presso tenantName il date alle time</p>"
          ),
          {
            customerNominative: getCustomerNominative(appointment.customer),
            tenantName: this.bootstrapService.getCurrentTenant()?.name || "",
            date: dateUtil(appointment.startAt)
              .locale(this.regionService.getLocale())
              .format("LL"),
            time: dateUtil(appointment.startAt)
              .locale(this.regionService.getLocale())
              .format("LT")
          }
        );

        // apre whatsapp
        reminderSendWhatsAppFromCalendar(
          {
            templateMessage: templateMessage,
            recipient: {
              phone: createReminderResult.phone!,
              customerId: appointment.customer.id
            },
            patientId: appointment.patient?.id
          },
          {
            customerBackend: this.customerBackend,
            reminderBackend: this.reminderBackend,
            toastService: this.toastService,
            bootstrapService: this.bootstrapService,
            documentService: this.documentService,
            patientBackend: this.patientBackend
          }
        );

        // crea il messaggio per lo storico di comunicazioni al cliente
        await lastValueFrom(
          this.messageBackend.createMessage({
            recipients: [
              {
                id: appointment!.customer?.id!,
                name: appointment!.customer?.firstName!,
                email: appointment!.customer?.email!,
                phone: createReminderResult.phone
              }
            ],
            sender: {
              id: this.bootstrapService.getCurrentTenant()!.id,
              name: this.bootstrapService.getCurrentTenant()!.name
            },
            transport: createReminderResult.transport,
            resources: compact([
              appointment.id,
              appointment.customer?.id,
              appointment.patient?.id
            ]),
            subject: lastName,
            priority: Priority.LOW,
            body: templateMessage
          })
        );
        break;
      }
    }
  }
}
